/* Competitor comparison: competitor tab switching + subtle scroll parallax. */
if (!customElements.get('competitor-comparison')) {
  class CompetitorComparison extends HTMLElement {
    constructor() {
      super();
      this._onScroll = this._onScroll.bind(this);
      this._onResize = this._onResize.bind(this);
      this._ticking = false;
    }

    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('.cc__tab'));
      this.compHeads = Array.from(this.querySelectorAll('.cc__comp-head'));
      this.compVals = Array.from(this.querySelectorAll('.cc__comp-val'));
      this.thumb = this.querySelector('.cc__tab-thumb');
      this._active = 1;

      this._initTabs();
      this._initParallax();

      // Place the sliding highlight once layout (and tab logos) are ready.
      requestAnimationFrame(() => this._moveThumb());
      window.addEventListener('load', this._onResize);
      window.addEventListener('resize', this._onResize, { passive: true });
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('load', this._onResize);
      window.removeEventListener('resize', this._onResize);
    }

    /* ---- Tabs ---- */
    _initTabs() {
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.setActive(tab.dataset.comp));
      });
    }

    setActive(index) {
      // Direction: next competitor slides in from the right, previous from the left.
      const dir = parseInt(index, 10) >= parseInt(this._active, 10) ? 1 : -1;
      this.style.setProperty('--cc-slide-from', dir * 28 + 'px');
      this._active = index;

      this.tabs.forEach((tab) => {
        const on = tab.dataset.comp === index;
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      this.compHeads.forEach((el) => el.classList.toggle('is-active', el.dataset.comp === index));
      this.compVals.forEach((el) => el.classList.toggle('is-active', el.dataset.comp === index));

      this._moveThumb();
    }

    /* Slide the highlight to sit exactly over the active tab. */
    _moveThumb() {
      if (!this.thumb || !this.tabs || !this.tabs.length) return;
      const active = this.tabs.find((t) => t.classList.contains('is-active')) || this.tabs[0];
      this.thumb.style.width = active.offsetWidth + 'px';
      this.thumb.style.height = active.offsetHeight + 'px';
      this.thumb.style.transform = 'translate(' + active.offsetLeft + 'px,' + active.offsetTop + 'px)';
    }

    _onResize() {
      this._moveThumb();
    }

    /* ---- Parallax ---- */
    _initParallax() {
      const enabled = this.dataset.parallax === 'true';
      const hasBg = this.dataset.hasBg === 'true';
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.intensity = parseFloat(this.dataset.parallaxIntensity || '0');

      if (!enabled || reduce || !hasBg || this.intensity <= 0) return;

      window.addEventListener('scroll', this._onScroll, { passive: true });
      this._update();
    }

    _onScroll() {
      if (this._ticking) return;
      this._ticking = true;
      window.requestAnimationFrame(() => {
        this._update();
        this._ticking = false;
      });
    }

    _update() {
      const rect = this.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      if (rect.bottom < 0 || rect.top > vh) return;
      // -1 (section below viewport) .. 1 (section above). 0 = centered.
      const progress = (rect.top + rect.height / 2 - vh / 2) / (vh / 2 + rect.height / 2);
      const y = Math.max(-1, Math.min(1, progress)) * -this.intensity;
      // Shift the section's background image vertically for a subtle parallax.
      this.style.backgroundPosition = 'center calc(50% + ' + y.toFixed(1) + 'px)';
    }
  }

  customElements.define('competitor-comparison', CompetitorComparison);
}
